<h3>
	<fa-icon [icon]="faImages" [fixedWidth]="true"></fa-icon>
	{{ "Treatment Files" | translate }} ({{ dataSource.data.length }})
	@if (config.app === "tmadmin") {
		<button
			mat-icon-button
			matTooltip="{{ 'RefreshFiles' | translate }}"
			(click)="onRefreshClicked()"
			[disabled]="isRefreshButtonDisabled"
			data-cy="refresh-files-button"
		>
			<mat-icon>refresh</mat-icon>
		</button>
	}
</h3>
<div class="table-controls">
	<mat-checkbox
		[disabled]="dataSource.data.length === 0"
		[checked]="allFilesChecked()"
		[indeterminate]="areSomeFilesSelected()"
		(change)="onAllFileSelectionChange($event.checked)"
		data-cy="select-all-files-checkbox"
	>
		{{ "SelectAll" | translate }}
	</mat-checkbox>
	<div class="button-container">
		@if (hasAssociatedTreatmentFiles) {
			<mat-checkbox
				[checked]="showAssociatedTreatmentFiles.value"
				(change)="onShowAssociatedTreatmentFilesClicked($event.checked)"
				data-cy="show-associated-treatment-files-checkbox"
			>
				{{ "ShowAssociatedTreatmentFiles" | translate }}
			</mat-checkbox>
		}
		<div
			matTooltip="{{'Preview3dFilesTooltip' | translate}}"
			[matTooltipDisabled]="!isMultipleFilesPreviewButtonDisabled()"
		>
			<button
				mat-raised-button
				disabled={{isMultipleFilesPreviewButtonDisabled()}}
				(click)="onMultipleFilesPreviewButtonClicked()"
				data-cy="preview-3d-files-button"
			>
				<fa-icon [icon]="faMagnifyingGlass"></fa-icon>
				{{ "Preview3dFiles" | translate }}
			</button>
		</div>
		<button
			mat-raised-button
			disabled="{{ selectedMediaFiles.length > 0 ? false : true }}"
			(click)="onDownloadSelectedFilesClicked()"
			data-cy="download-files-button"
			>
			<fa-icon [icon]="faDownload"></fa-icon>
			{{ "DownloadFiles" | translate }}
		</button>
	</div>
</div>
<div class="table-filters">
	<mat-form-field [ngClass]="{'highlighted': !!dataSource.filter}">
		<mat-icon matPrefix>search</mat-icon>
		<input
			matInput
			placeholder="{{ 'Search' | translate }}"
			[(ngModel)]="searchQuery"
			(ngModelChange)="applyFilter($event)"
			data-cy="text-filter-input"
		/>
		@if(dataSource.filter) {
			<button
				matSuffix
				mat-icon-button
				(click)="clearFilter(); $event.stopPropagation()"
				data-cy="text-filter-clear-button"
			>
				<mat-icon>close</mat-icon>
			</button>
		}
	</mat-form-field>
</div>

@if (dataSource.data.length) {
	<div class="table-wrapper" [ngStyle]="{'--numFiles': dataSource.data.length}">
		<mat-table [dataSource]="dataSource" matSort class="media-list">

			<!-- Checkbox -->
			<ng-container matColumnDef="checkbox">
				<mat-header-cell *matHeaderCellDef></mat-header-cell>
				<mat-cell *matCellDef="let file">
					<mat-checkbox
						(change)="onFileSelectionChange($event.checked, file)"
						[checked]="isFileSelected(file)"
					></mat-checkbox>
				</mat-cell>
			</ng-container>

			<!-- Filename -->
			<ng-container matColumnDef="filename">
				<mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'Filename' | translate }}</mat-header-cell>
			<mat-cell *matCellDef="let file" [title]="file.orgFilename">
				@if (isFileFromAssociatedTreatment(file)) {
					<fa-icon class="associated-treatment-icon" [icon]="faDiagramProject" [title]="'FromAssociatedTreatment' | translate"></fa-icon>
				}
				@if (isFileFromAssociatedTicket(file)) {
					<fa-icon class="associated-ticket-icon" [icon]="faTags" [title]="'FromAssociatedTicket' | translate"></fa-icon>
				}
				<span class="filename-text">{{ file.orgFilename }}</span>
			</mat-cell>
			</ng-container>

			<!-- Upload Type -->
			<ng-container matColumnDef="uploadType">
				<mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'Type' | translate }}</mat-header-cell>
				<mat-cell *matCellDef="let file">{{ getUploadTypeLabel(file.uploadType) | translate | uppercase }}</mat-cell>
			</ng-container>

			<!-- File Extension -->
			<ng-container matColumnDef="fileType">
				<mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'Ext' | translate }}</mat-header-cell>
				<mat-cell *matCellDef="let file">{{ file.filetype | uppercase }}</mat-cell>
			</ng-container>

			<!-- Doc / Tech -->
			<ng-container matColumnDef="mediaType">
				<mat-header-cell *matHeaderCellDef mat-sort-header><fa-icon [icon]="faUser"></fa-icon></mat-header-cell>
				<mat-cell *matCellDef="let file">{{ file.type === MediaTypes.User ? "DOC" : "TEC" }}</mat-cell>
			</ng-container>

			<!-- File size -->
			<ng-container matColumnDef="filesize">
				<mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'Size' | translate }}</mat-header-cell>
				<mat-cell *matCellDef="let file" class="align-right">{{ getFileSizeAsString(file.filesize) }}</mat-cell>
			</ng-container>

			<!-- Created date -->
			<ng-container matColumnDef="created">
				<mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'Date' | translate }}</mat-header-cell>
				<mat-cell *matCellDef="let file" [title]="file.created | date: 'medium' : 'EUROPE/BERLIN' : getLocale()">
					{{ file.created | date: "short" : "EUROPE/BERLIN" : getLocale() }}
				</mat-cell>
			</ng-container>

			<!-- Preview -->
			<ng-container matColumnDef="preview">
				<mat-header-cell *matHeaderCellDef></mat-header-cell>
				<mat-cell *matCellDef="let file">
					@if (isFilePreviewAvailable(file.filetype)) {
						<a (click)="openFilePreview(file)" [title]="'Preview file' | translate">
							<fa-icon [icon]="faMagnifyingGlass"></fa-icon>
						</a>
					} @else {
						<fa-icon [icon]="faMagnifyingGlass" class="greyed-out"></fa-icon>
					}
				</mat-cell>
			</ng-container>

			<!-- Download -->
			<ng-container matColumnDef="download">
				<mat-header-cell *matHeaderCellDef></mat-header-cell>
				<mat-cell *matCellDef="let file">
					<a (click)="downloadMediaFile(file._id, file.orgFilename)" [title]="'Download file' | translate">
						<fa-icon [icon]="faDownload"></fa-icon>
					</a>
				</mat-cell>
			</ng-container>

			<!-- Visibility toggle (admins only) -->
			<ng-container matColumnDef="hide">
				<mat-header-cell *matHeaderCellDef></mat-header-cell>
				<mat-cell *matCellDef="let file">
					<a (click)="hideFile(file)" [title]="'Show/hide in customer frontend' | translate">
						<fa-icon [icon]="file.visibilityScope === VisibilityScope.PUBLIC ? faEye : faEyeSlash"></fa-icon>
					</a>
				</mat-cell>
			</ng-container>

			<!-- Delete (super admins only) -->
			<ng-container matColumnDef="delete">
				<mat-header-cell *matHeaderCellDef></mat-header-cell>
				<mat-cell *matCellDef="let file">
					<a (click)="openDeleteConfirmationDialog(file._id)" [title]="'Delete file' | translate">
						<fa-icon [icon]="faTrashAlt"></fa-icon>
					</a>
				</mat-cell>
			</ng-container>

			<mat-header-row *matHeaderRowDef="displayedColumns(); sticky: true"></mat-header-row>
			<mat-row
				*matRowDef="let row; columns: displayedColumns();"
				[class.greyed-out]="isFileGreyedOut(row)"
				[class.associated-treatment]="isFileFromAssociatedTreatment(row)"
			></mat-row>
		</mat-table>
	</div>
} @else {
	<p>{{ "no files" | translate }}</p>
}
